import { appState } from "@/app/appState";
import { ChatArtifact, ChatEvent, chatState, ChatTag } from "@/app/chatState";
import { EntryItem, useRefState, DateTimeRangeValue } from "@/components/UI/tools";
import { useCallback, useEffect, useMemo, useState } from "react";
import { removeEmptyLikeFields, setHasChagedCallback, shortenLabel } from "../chatObjectEditorWindow";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { calendar_event, calendar_event_list_item } from "@/app/typing/events";
import { chatUpdateEvent, chatDeleteEvent } from "@/api/sdk.gen";
import { ChatUpdateEventData, ChatDeleteEventData } from "@/api/types.gen";
import { Request } from "@/components/CRUD";
import { Org, User } from "@/components/UI/svgs";
import { tag_list_item } from "@/app/typing/tags";
import { artifact_list_item } from "@/app/typing/artifacts";
import { arrayDiff } from "diff";
import { ArtifactEditor } from "./artifactEditor";
import { TranscriptionComponent } from "@/components/TranscriptionComponent";

export const EventEditor = observer(() => {
    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [id, setId] = useState<number | undefined>(undefined);
    const [edit, setEdit] = useState(false);

    const [revisionId, setRevisionId] = useState<number | undefined>(undefined);
    const [revisionPage, setRevisionPage] = useState(0);
    const [maxRevPage, setMaxRevPage] = useState(0);

    const objState = useMemo(() => {
        return runInAction(() => {
            const key = chatState.selectedObjectKey;
            setRefresh((prev) => prev + 1);

            if (key && key.type === "event") {
                const obj = chatState.chatObjects.get(key) as ChatEvent | undefined;

                if (!obj || !obj.event) {
                    chatState.getObjectByKey({ type: "event", id: key.id });
                    return undefined;
                }

                let revision_page = obj.revision_index !== undefined ? obj.revision_index : -1;
                setRevisionPage(revision_page);

                const revisions = obj.revisions?.slice().sort((a, b) => a.id - b.id).filter(x => x ? true : false) ?? [];
                setMaxRevPage(revisions.length);

                const ev = obj.event as calendar_event;

                if (revisions.length > 0) {
                    if (revision_page > revisions.length) return undefined;

                    const maxIndex = revision_page + 1;
                    const isLastRevision = maxIndex === revisions.length;
                    const isRoot = revision_page === -1;

                    setEdit(isLastRevision);
                    const curr = revisions[revision_page] ?? ev;

                    setId(obj.id);
                    setRevisionId(isRoot ? undefined : curr.id);

                    const old_obj = {
                        title: curr.title,
                        artifact_ids: ev.artifact_ids,
                        description: curr.description,
                        location: curr.location,
                        start_time: curr.start_time,
                        end_time: curr.end_time,
                        all_day: curr.all_day,
                        timezone: curr.timezone,
                        tag_id: ev.tag_id,
                    };

                    if (obj.child_index === undefined) setHasChagedCallback(old_obj);

                    if (isLastRevision) {
                        return { ...old_obj, ...removeEmptyLikeFields(obj.temp_obj) };
                    } else {
                        return { ...old_obj };
                    }
                } else {
                    setId(obj.id);
                    setRevisionId(undefined);
                    setEdit(obj.unsaved_changes ? true : (obj.edit ?? false));



                    const old_obj = {
                        title: ev.title,
                        artifact_ids: ev.artifact_ids,
                        description: ev.description,
                        location: ev.location,
                        start_time: ev.start_time,
                        end_time: ev.end_time,
                        all_day: ev.all_day,
                        timezone: ev.timezone,
                        tag_id: ev.tag_id,
                    };

                    console.log("SETTING HAS CHANGED CALLBACK FOR NON REVISION EVENT", old_obj)

                    if (obj.child_index === undefined) setHasChagedCallback(old_obj);

                    return {
                        ...old_obj,
                        ...removeEmptyLikeFields(obj.temp_obj),
                    };
                }
            }
            return undefined;
        });
    }, [
        chatState.selectedObjectKey,
        (chatState.chatObjects.get(chatState.selectedObjectKey) as ChatEvent)?.event,
        (chatState.chatObjects.get(chatState.selectedObjectKey) as ChatEvent)?.event?.artifact_ids,
        (chatState.chatObjects.get(chatState.selectedObjectKey) as ChatEvent)?.revision_index,
        chatState.chatObjects.get(chatState.selectedObjectKey)?.edit,
        chatState.chatObjects.get(chatState.selectedObjectKey)?.revisions?.length,
        chatState.chatObjects.get(chatState.selectedObjectKey)?.child_index,
        chatState.lastExternalUpdate,
    ]);

    const currentObj: any = {
        ...objState,
        ...removeEmptyLikeFields(chatState.chatObjects.get(chatState.selectedObjectKey)?.temp_obj),
    };

    useEffect(() => {

        chatState.getObjectByKey({ type: "tag", id: currentObj?.tag_id });

    }, [currentObj.tag_id]);

    const publishRevision = useCallback(async () => {
        setLoading(true);
        const isRoot = revisionId === undefined;
        const isLastRevision = revisionPage === (maxRevPage - 1);

        if (!objState) {
            setLoading(false);
            return;
        }

        try {
            if (id && chatState.conversationId) {
                const response: any = await chatUpdateEvent({
                    body: {
                        id: id,
                        conversation_id: chatState.conversationId,
                        title: currentObj?.title ?? "",
                        description: currentObj?.description ?? null,
                        location: currentObj?.location ?? null,
                        start_time: currentObj?.start_time,
                        end_time: currentObj?.end_time,
                        all_day: currentObj?.all_day ?? false,
                        timezone: currentObj?.timezone ?? "UTC",
                        revision_id: revisionId ?? null
                    }
                });

                if (response.data) {
                    runInAction(() => {
                        if (isRoot || isLastRevision) {
                            chatState.chatObjects.update(
                                chatState.selectedObjectKey
                                    ? [
                                        {
                                            key: chatState.selectedObjectKey,
                                            value: {
                                                unsaved_changes: false,
                                                event: {
                                                    title: currentObj?.title,
                                                    description: currentObj?.description,
                                                    location: currentObj?.location,
                                                    start_time: currentObj?.start_time,
                                                    end_time: currentObj?.end_time,
                                                    all_day: currentObj?.all_day,
                                                    timezone: currentObj?.timezone,
                                                },
                                            },
                                        },
                                    ]
                                    : []
                            );
                            chatState.setObjectEditorTemp(null)

                        }
                        if (chatState.selectedObjectKey) {
                            chatState.getObjectByKey({ type: "event", id: chatState.selectedObjectKey.id });
                        }
                        chatState.chatObjects.update(chatState.selectedObjectKey ? [{ key: chatState.selectedObjectKey, value: { revision_index: -1 } }] : []);
                        if (id) {
                            chatState.flushRevisions({ type: "event", id: id }, revisionId);
                        }
                        appState.getEvents({ pageNumber: 0, search: "" });
                    });

                    runInAction(() => {
                        appState.chat_refresh = appState.chat_refresh + 1;
                        chatState.setSelectedObjectKey({ type: 'event', id: id }, true);
                    });
                    setEdit(false);
                    return true;
                } else {
                    if (response.error) {
                        console.error("Error occurred:", response);
                        appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorUpdateEvent") + " - " + response.error.detail });
                        setRefresh((p) => p + 1);
                    }
                }
            } else {
                console.error("Missing required field!", "id", id);
            }
        } finally {
            setLoading(false);
        }
    }, [id, currentObj.title, currentObj.description, currentObj.location, currentObj.start_time, currentObj.end_time, currentObj.all_day, currentObj.timezone, revisionId, revisionPage, maxRevPage, objState]);

    const updateEvent = Request<ChatUpdateEventData, any>(
        chatUpdateEvent,
        () => ({
            body: {
                id: id!,
                conversation_id: chatState.conversationId!,
                title: currentObj?.title ?? "",
                description: currentObj?.description ?? null,
                location: currentObj?.location ?? null,
                start_time: currentObj?.start_time,
                end_time: currentObj?.end_time,
                all_day: currentObj?.all_day ?? false,
                timezone: currentObj?.timezone ?? "UTC",
            },
        }),
        setLoading,
        (data) => {
            runInAction(() => {
                chatState.chatObjects.update(
                    chatState.selectedObjectKey
                        ? [
                            {
                                key: chatState.selectedObjectKey,
                                value: {
                                    unsaved_changes: false,
                                    event: {
                                        title: objState?.title,
                                        description: objState?.description,
                                        location: objState?.location,
                                        start_time: objState?.start_time,
                                        end_time: objState?.end_time,
                                        all_day: objState?.all_day,
                                        timezone: objState?.timezone,
                                    },
                                },
                            },
                        ]
                        : []
                );
                chatState.setObjectEditorTemp(null)

                if (chatState.selectedObjectKey) {
                    chatState.flushRevisions({ type: "event", id: chatState.selectedObjectKey.id })
                    chatState.getObjectByKey({ type: "event", id: chatState.selectedObjectKey.id });
                }
            });
            setEdit(false);
        },
        (error) => {
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorUpdateEvent"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            });
        },
        () => id !== undefined && id > 0 && chatState.conversationId !== null,
    );

    // Format ISO datetime to local datetime-local input value
    const toDateTimeLocal = (iso: string | null | undefined): string => {
        if (!iso) return "";
        try {
            const d = new Date(iso);
            // Format: YYYY-MM-DDTHH:MM
            const pad = (n: number) => n.toString().padStart(2, "0");
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        } catch {
            return iso;
        }
    };

    const dateOptions: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "numeric",
        day: "numeric",
    };

    const activeObj = chatState.selectedObjectKey ? chatState.chatObjects.get(chatState.selectedObjectKey) : undefined;
    const childIndex = (activeObj as any)?.child_index;

    const treeNodes = chatState.selectedObjectKey ? chatState.chatObjects.entries([chatState.selectedObjectKey as any]) : [];
    const treeNode = treeNodes.length > 0 ? treeNodes[0] : undefined;

    if (childIndex !== undefined && treeNode?.children && treeNode.children.length > childIndex) {
        const childKey = treeNode.children[childIndex].key;
        switch (childKey.type) {
            case "artifact":
                return <ArtifactEditor key={`${childKey.type}-${childKey.id}`} objectKey={childKey} />; //key here is to force remount when switching to a different child - otherwise temp state from previous child will persist and cause bugs
            // Future handlers can be added here
        }
    }

    const transcriptionScroll = chatState.pendingTranscriptionScroll;
    const scrollForThisEvent = transcriptionScroll?.eventId === id ? transcriptionScroll : undefined;

    return id && id === chatState.selectedObjectKey?.id ? (
        <div className="flex flex-col pb-1">

            <div className="shrink-0">
                <EntryItem
                isRevisionRoot={(revisionId === undefined) && (maxRevPage > 0)}
                forcedEdit={maxRevPage > 0 ? true : false}
                unsavedChanges={(chatState.chatObjects.get(chatState.selectedObjectKey!) as ChatEvent)?.unsaved_changes ?? false}
                lock={false}
                setEdit={(val: boolean) => {
                    chatState.chatObjects.update(
                        chatState.selectedObjectKey
                            ? [{ key: chatState.selectedObjectKey, value: { edit: val } }]
                            : []
                    );
                }}
                edit={edit}
                name={appState.t("objects.list.event")}
                created_at={new Date(
                    (chatState.chatObjects.get(chatState.selectedObjectKey!) as ChatEvent)?.event?.created_at ?? ""
                ).toLocaleDateString(undefined, dateOptions)}
                loading={loading}
                deleting={deleting}
                readonly={false}
                chatEditorWindowClose={() => {
                    chatState.removeSelectedObjectKey();
                }}
                updateEntry={maxRevPage > 0 ? () => publishRevision() : updateEvent}
                refreshCount={refresh}
                idParams={{
                    singular: { get: chatState.selectedObjectKey?.id, set: () => { } },
                    plural: { get: [], set: () => { } },
                }}
                nameColumn={{
                    type: "text",
                    value: currentObj?.title ?? "",
                    onChange: (x) => chatState.setObjectEditorTemp({ title: x } as any),
                    validator: (val: string) => val.length > 0,
                    label: appState.t("objects.title"),
                }}
                refColumns={[
                    {
                        type: "textarea",
                        label: appState.t("objects.description"),
                        value: currentObj?.description ?? "",
                        onChange: (value) => chatState.setObjectEditorTemp({ description: value } as any),
                        validator: () => true,
                    },
                    {
                        label: appState.t("objects.location"),
                        type: "text",
                        onChange: (x) => chatState.setObjectEditorTemp({ location: x } as any),
                        value: currentObj?.location ?? "",
                        validator: () => true,
                    },
                ]}
                stateColumns={[
                    {
                        label: appState.t("objects.when"),
                        type: "datetimerange" as const,
                        onChange: (val: DateTimeRangeValue) => {
                            chatState.setObjectEditorTemp({ start_time: val.startTime, end_time: val.endTime } as any);
                        },
                        value: { startTime: currentObj?.start_time ?? "", endTime: currentObj?.end_time ?? "" },
                        validator: (val: DateTimeRangeValue) => val.startTime.length > 0 && val.endTime.length > 0,
                        allDay: currentObj?.all_day ?? false,
                        onAllDayChange: (val: boolean) => {
                            chatState.setObjectEditorTemp({ all_day: val } as any);
                        },
                    },
                    {
                        label: appState.t("objects.tag"),
                        type: "single",
                        onChange: () => { },
                        value: currentObj?.tag_id,
                        items: [...(appState.tags ?? []), ...chatState.chatObjects.entries().filter(x => x.value.type === "tag" && (x.value as ChatTag).tag && !(appState.tags ?? []).find(t => t.id === x.value.id)).map(x => (x.value as ChatTag).tag as any as tag_list_item)].filter((x) => [4, 5, 6, 7].includes(x.effective_permission_type)).map((x) => ({
                            id: x.id,
                            name: x.name,
                            children: (
                                <div className="flex flex-row gap-[0.5rem] items-center">
                                    <>{shortenLabel(x.name)}</>
                                    {x.scope == 'me' ? <User size={16} /> : <Org size={16} />}
                                </div>
                            ),
                            defaultValue: currentObj?.tag_ids?.includes(x.id)
                        })),
                        validator: () => true,
                        readonly: true,
                    },
                    {
                        label: appState.t("objects.artifacts"),
                        readonly: true,
                        type: "multiple",
                        onChange: () => { },
                        value: currentObj?.artifact_ids ?? [],
                        items: [...(appState.artifacts ?? []), ...[...chatState.chatObjects.values()].filter(x => x.type === "artifact" && (x as ChatArtifact).artifact && !(appState.artifacts ?? []).find(t => t.id === x.id)).map(x => (x as ChatArtifact).artifact as any as artifact_list_item)].map((x) => ({
                            id: x.id,
                            name: x.variable_name,
                            children: (
                                <div className="flex flex-row gap-[0.5rem] items-center">
                                    <>{shortenLabel(x.variable_name)}</>
                                </div>
                            ),
                            defaultValue: currentObj?.artifact_ids?.includes(x.id)
                        })),
                        validator: () => true,
                    }
                ]}
            />
            </div>
            <div className="entry-inset-x">
                <TranscriptionComponent
                    key={id}
                    eventId={id}
                    scrollToLine={scrollForThisEvent?.startLine}
                    scrollToEndLine={scrollForThisEvent?.endLine}
                    scrollNonce={scrollForThisEvent?.nonce}
                />
            </div>
        </div>
    ) : null;
});
